// Styles globaux du design system — importés **une seule fois**, ici, au point d'entrée
// (cf. packages/ui/CLAUDE.md). Un composant ne doit jamais embarquer cette feuille.
import '@ghp/ui/styles'

import { createApiClient, readApiBaseUrl } from '@ghp/api-client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { ToastProvider } from '@ghp/ui'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createAuthStore } from './features/auth/auth-store'
import { scopeOf } from './features/auth/session'
import { createGeolocationPort } from './features/inspections/ports/geolocation'
import { createPendingUpdate } from './offline/pending-update'
import { openSessionDatabase } from './features/auth/session-store'
import { deleteRetiredDatabase } from './offline/retired-data'
import { openSharedDatabase } from './offline/shared-db'
import { createTeamDataStore } from './offline/team-data'
import {
  DRAFTS_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  INTERNAL_MEETINGS_QUERY_KEY,
  SERVER_RULES_QUERY_KEY,
  VOCABULARY_QUERY_KEY,
} from './offline/query-keys'
import { installLogCapture, recordBoot, recordEvent } from './offline/logger'
import { createSyncSteps, reportSyncFailure, startSyncScheduler } from './offline/scheduler'
import { registerServiceWorker } from './offline/service-worker'
import { runAfterPageLoad } from './offline/run-after-page-load'
import { watchLocalData } from './offline/watch'
import { createQueryClient } from './app/query-client'
import { createAppRouter } from './app/router'
import { startErrorReporting } from './app/error-reporting'

// Avant tout le reste : une erreur au démarrage est la première qu'on veut voir (D15).
startErrorReporting(import.meta.env)

/**
 * Point d'entrée de l'app terrain.
 *
 * Ordre volontaire : on monte React, puis on **programme** l'enregistrement du Service
 * Worker pour l'événement `load`. Enregistrer sur-le-champ ne suffirait pas à sortir du
 * chemin critique : le précache Workbox met aussitôt en file tout l'*app shell*, et sur
 * la 4G d'un chantier ces requêtes concurrencent celles du premier rendu. L'agent voit
 * donc son interface d'abord ; la mise en cache du shell vient après, page chargée.
 */

/**
 * La session (D22 pt.1) : entrée sur la lecture locale seule, montée en ligne déclenchée par le
 * planificateur de synchro, qui en fait sa première étape. Le store ne le fait pas de lui-même :
 * l'appel réseau reste un geste extérieur, hors du chemin d'entrée.
 */
/**
 * La base que **toutes les équipes partagent** : le référentiel d'anomalies, le journal de
 * diagnostic et les réglages de l'appareil (`offline/shared-db.ts`). Rien de ce qu'elle porte
 * n'est la saisie de quelqu'un — les tournées, elles, vivent dans une base par équipe, ouverte
 * plus bas quand la session dit laquelle.
 *
 * Ouverte **en premier**, et avant toute session : le journal de diagnostic s'y branche, et une
 * erreur de démarrage est exactement celle qu'on ne peut pas reproduire au bureau (D15).
 */
const shared = openSharedDatabase()
installLogCapture(shared)
recordBoot(shared)
void deleteRetiredDatabase()

/**
 * `onCall` journalise **chaque** appel HTTP — méthode, chemin templatisé, statut, durée : la
 * matière première de tout diagnostic réseau. Les identifiants sortent du chemin (ce sont des
 * dimensions du `detail` ailleurs), et la route du journal elle-même est exclue : chaque
 * expédition de lot produirait sinon la ligne suivante, sans fin.
 */
const api = createApiClient({
  baseUrl: readApiBaseUrl(import.meta.env),
  onCall: (call) => {
    if (call.path.startsWith('/sync/logs')) return

    const path = call.path.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id').replace(/\?.*$/, '')
    recordEvent(
      shared,
      call.ok ? 'info' : 'warn',
      'api.call',
      `${call.method} ${path} → ${String(call.status)}`,
      { detail: { method: call.method, path, status: call.status, durationMs: call.durationMs } },
    )
  },
})
const auth = createAuthStore(openSessionDatabase(), api.auth)

const queryClient = createQueryClient()

/**
 * Ce que la synchro et les écrans lisent de la session : le jeton d'accès **s'il y en a un**.
 *
 * Une session dégradée n'en a pas, et c'est un état de fonctionnement normal (D22 pt.1), pas une
 * anomalie : la synchro n'a alors rien à faire, le retour du réseau ou le tour de minuteur
 * suivant la rappellera.
 */
const currentAccessToken = (): string | null => {
  const session = auth.getSnapshot()

  return session.state === 'online' ? session.accessToken : null
}

/**
 * Les bases de tournée, **une par équipe** (D22 pt.4) : le porteur suit la session et n'en tient
 * qu'une ouverte. C'est lui, et lui seul, qui fait qu'une équipe ne lit pas les données d'une
 * autre — sa base n'est pas ouverte.
 *
 * ⚠️ Il lit la session à travers une forme structurelle, pas le store lui-même : `offline/` est
 * la couche la plus basse de l'app et ne connaît pas `features/auth`. Le point d'entrée branche
 * les deux, ici.
 *
 * ⚠️ `queryClient.clear()` au changement d'équipe, et c'est la moitié du cloisonnement que les
 * bases ne portent pas : le cache des lectures vit en mémoire, ne périme jamais et le départ d'un
 * agent **navigue** sans recharger la page. Sans ce vidage, l'équipe entrante se ferait servir
 * depuis le cache ce que la sortante y avait chargé, sans qu'aucune base ne soit relue.
 */
const data = createTeamDataStore(
  {
    get: () => scopeOf(auth.getSnapshot()),
    subscribe: (listener: () => void) => auth.subscribe(listener),
  },
  { onTeamChange: () => queryClient.clear() },
)

/**
 * Quand la synchro repart, et l'isolation de ses étapes : `offline/scheduler.ts`. Ici, on
 * branche — le point d'entrée n'arbitre rien.
 *
 * `scheduler.requestSync()` est le déclencheur que l'éditeur de contrôle appellera après une
 * transmission, pour ne pas faire attendre l'agent jusqu'au tour de minuteur suivant.
 */
const scheduler = startSyncScheduler(
  createSyncSteps({
    data,
    shared,
    // ⚠️ **Quatre familles, parce que les routes sont dans quatre modules du serveur** : le dépôt
    // d'un contrôle vit sous `sync`, l'avis de passage sous `passage-notices`, le rendez-vous pris
    // sur place sous `planning`, l'étape d'un contrôle sous `inspection-steps`. Les fusionner ici est ce que la passe attend, et c'est le seul
    // endroit où le vrai client les rapproche.
    api: {
      ...api.sync,
      record: api.passageNotices.record,
      recordFieldAppointment: api.planning.recordFieldAppointment,
      recordInspectionStep: api.inspectionSteps.record,
    },
    getAccessToken: currentAccessToken,
    // Le refresh d'abord : c'est lui qui rend un jeton d'accès frais à une session dégradée.
    refreshSession: () => auth.retryRefresh(),
    // Un 401 en cours de synchro : la session redescend en dégradé, et le refresh de la
    // passe suivante repart avec le refresh token — au lieu de rejouer le jeton mort.
    onUnauthorized: (token) => auth.tokenRefused(token),
    // Invalidation **explicite à l'endroit qui écrit** (D21) : ces tables n'ont pas
    // d'observateur `liveQuery`, une descente en arrière-plan doit pousser l'écran elle-même —
    // sinon « Mes contrôles » n'affiche la tournée qu'à la navigation suivante.
    onPulled: (table) => {
      if (table === 'vocabulary') {
        void queryClient.invalidateQueries({ queryKey: VOCABULARY_QUERY_KEY })
        return
      }

      if (table === 'internalMeetings') {
        void queryClient.invalidateQueries({ queryKey: INTERNAL_MEETINGS_QUERY_KEY })
        return
      }

      // Les brouillons partent avec la tournée : `openDraft` met `null` en cache pour une
      // demande inconnue, et `liveQuery` n'observe que `db.drafts` — sans cette invalidation,
      // un éditeur resté sur « Contrôle inconnu » ne rouvrirait jamais, même la tournée
      // descendue, tant que l'app n'est pas rechargée.
      void queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
      // Les règles du serveur (M35, pièces après transmission) descendent avec la tournée, et
      // leur lecture est en `staleTime: Infinity` : sans elle, l'éditeur gardait l'ancienne
      // valeur jusqu'au rechargement.
      void queryClient.invalidateQueries({ queryKey: SERVER_RULES_QUERY_KEY })
    },
  }),
)

/**
 * La première passe attend la **lecture locale** de la session, pas le réseau (D22 pt.1). Si
 * même cette lecture échoue, l'app reste utilisable et le minuteur du planificateur rappellera
 * la synchro : rien ne remonte en `unhandledrejection`.
 */
void auth.ready.then(
  () => scheduler.requestSync(),
  (error: unknown) => reportSyncFailure('session', error),
)

// Toute écriture locale — y compris celle d'un rejeu en arrière-plan — rafraîchit l'écran (D21).
// Les observations suivent la base ouverte : le porteur les prévient d'un changement
// d'équipe, et une observation posée sur une base refermée ne se réveillerait plus jamais.
watchLocalData(data, queryClient)

/**
 * Le relais entre Workbox et l'écran : la version précachée qui **attend** l'accord de l'agent
 * (#224). Créé ici parce qu'il a deux extrémités et qu'elles ne se connaissent pas — le Service
 * Worker y dépose, la coquille y lit. Sans lui, `applyUpdate` n'était appelable de nulle part et
 * un déploiement n'atteignait jamais un onglet ouvert.
 */
const pendingUpdate = createPendingUpdate()

const router = createAppRouter(queryClient, auth, {
  data,
  shared,
  sync: api.sync,
  contacts: { updateContacts: api.inquiries.updateContacts },
  planning: { recordFieldAppointment: api.planning.recordFieldAppointment },
  accessToken: currentAccessToken,
  geolocation: createGeolocationPort(),
  requestSync: () => scheduler.requestSync(),
  pendingUpdate,
})

const root = document.getElementById('root')

if (!root) {
  // Cas impossible en fonctionnement normal : l'élément est dans `index.html`. Échouer
  // bruyamment vaut mieux qu'une page blanche silencieuse sur une tablette de terrain.
  throw new Error('Montage impossible : élément #root introuvable dans index.html.')
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Les confirmations du prototype (« Contrôle archivé »…) : une seule pile pour tous les écrans. */}
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)

runAfterPageLoad(() => registerServiceWorker(pendingUpdate.announce))
